import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { addTotalBillValue, removeTotalBillValue, clearTotalBillValues } from '../store/total-bill-slice';
import Button from '../components/Button';
import { notificationsliceactions } from '../store/notification';

const TotalBillManager = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { totalBillValues } = useSelector(state => state.totalBill);
    const [inputValue, setInputValue] = useState('');
    const inputRef = useRef();
    const [showInstructions, setShowInstructions] = useState(false);

    // Auto-focus the input when component mounts
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    const handleInputChange = (e) => {
        setInputValue(e.target.value);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleAddValue();
        }
    };

    const handleAddValue = () => {
        const value = parseFloat(inputValue);
        if (isNaN(value) || value <= 0) {
            dispatch(notificationsliceactions.updatenotification({
                message: 'Please enter a valid positive number',
                status: 'error'
            }));
            return;
        }

        dispatch(addTotalBillValue(value));
        setInputValue('');
        
        // Focus back to input after adding
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 100);

        dispatch(notificationsliceactions.updatenotification({
            message: `Bill value ${value} added successfully`,
            status: 'Success'
        }));
    };

    const handleRemoveValue = (index) => {
        dispatch(removeTotalBillValue(index));
        dispatch(notificationsliceactions.updatenotification({
            message: 'Total Bill value removed',
            status: 'info'
        }));
    };

    const handleClearAll = () => {
        if (totalBillValues.length === 0) {
            dispatch(notificationsliceactions.updatenotification({
                message: 'No values to clear',
                status: 'info'
            }));
            return;
        }

        if (window.confirm('Are you sure you want to clear all Total Bill values?')) {
            dispatch(clearTotalBillValues());
            dispatch(notificationsliceactions.updatenotification({
                message: 'All Total Bill values cleared',
                status: 'info'
            }));
        }
    };

    return (
        <div style={{ padding: '0px', maxWidth: '600px', minWidth: '500px', margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
                <h2 style={{ textAlign: 'center', margin: 0 }}>
                    Total Bill Manager<br />(Max: 25/Current: {totalBillValues.length})
                </h2>
                <span
                    title="Show instructions"
                    style={{ cursor: 'pointer', fontSize: '22px', color: '#1976d2', marginLeft: '6px', userSelect: 'none' }}
                    onClick={() => setShowInstructions(true)}
                >
                    ℹ️
                </span>
            </div>
            <Button
                    disabled={totalBillValues.length === 0}
                    clickhandler={() => navigate('../create')}
                    cs={{
                        padding: '10px 20px',
                        marginRight: '10px',
                        backgroundColor: totalBillValues.length === 0 ? 'rgba(0,0,0,0.6)' : undefined,
                        borderColor: totalBillValues.length === 0 ? 'rgba(0,0,0,0.6)' : undefined,
                    }}
                    title={totalBillValues.length === 0 ? 'Add at least 1 Total Bill value to enable Create Bill' : 'Go to Create Bill'}
                >
                    Go to Create Bill
                </Button>

            {/* Instructions Modal */}
            {showInstructions && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '24px',
                        borderRadius: '10px',
                        maxWidth: '90%',
                        minWidth: '320px',
                        boxShadow: '0 4px 16px rgba(25, 118, 210, 0.15)',
                        position: 'relative',
                        fontSize: '15px'
                    }}>
                        <h3 style={{ marginTop: 0, marginBottom: '10px', color: '#1976d2' }}>Instructions</h3>
                        <ul style={{ margin: '10px 0 20px 0', paddingLeft: '20px' }}>
                            <li>Enter a numeric value and press Enter or click Add</li>
                            <li>The first (oldest) value will be used as the default Total Bill in Create Bill page</li>
                            <li>Values are stored in order (oldest first)</li>
                            <li>Maximum 25 values are kept</li>
                            <li>Data is automatically saved to browser storage and persists across sessions</li>
                        </ul>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button 
                                clickhandler={() => setShowInstructions(false)}
                                cs={{ 
                                    backgroundColor: '#2196f3', 
                                    borderColor: '#2196f3',
                                    color: 'white',
                                    padding: '8px 20px',
                                    fontSize: '15px'
                                }}
                            >
                                Close
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            <div style={{ 
                display: 'flex', 
                gap: '10px', 
                alignItems: 'center', 
                marginBottom: '10px',
                padding: '20px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                backgroundColor: '#f9f9f9'
            }}>
                <input
                    ref={inputRef}
                    type="number"
                    placeholder="Enter Total Bill amount"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    style={{
                        flex: 1,
                        padding: '12px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        fontSize: '16px'
                    }}
                />
                <Button 
                    clickhandler={handleAddValue}
                    cs={{ padding: '12px 20px', fontSize: '16px' }}
                >
                    Add
                </Button>
            </div>

            {/* Values List */}
            <div style={{ marginBottom: '20px' }}>
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '15px'
                }}>
                    <h3>Total Bill Values ({totalBillValues.length})</h3>
                    {totalBillValues.length > 0 && (
                        <Button 
                            clickhandler={handleClearAll}
                            cs={{ 
                                padding: '8px 16px', 
                                backgroundColor: '#f44336',
                                borderColor: '#f44336',
                                color: 'white'
                            }}
                        >
                            Clear All
                        </Button>
                    )}
                </div>

                {totalBillValues.length === 0 ? (
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '40px', 
                        color: '#666',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '4px'
                    }}>
                        No Total Bill values added yet
                    </div>
                ) : (
                    <div style={{ 
                        border: '1px solid #ddd', 
                        borderRadius: '4px',
                        overflow: 'hidden'
                    }}>
                        {totalBillValues.map((value, index) => (
                            <div 
                                key={index}
                                style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center',
                                    padding: '12px 15px',
                                    borderBottom: index < totalBillValues.length - 1 ? '1px solid #eee' : 'none',
                                    backgroundColor: index === 0 ? '#e8f5e8' : 'white'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ 
                                        fontWeight: 'bold', 
                                        color: index === 0 ? '#2e7d32' : '#333',
                                        fontSize: '16px'
                                    }}>
                                        ₹{value}
                                    </span>
                                    {index === 0 && (
                                        <span style={{ 
                                            fontSize: '12px', 
                                            color: '#2e7d32',
                                            backgroundColor: '#c8e6c9',
                                            padding: '2px 8px',
                                            borderRadius: '12px'
                                        }}>
                                            Current Default
                                        </span>
                                    )}
                                </div>
                                <Button 
                                    clickhandler={() => handleRemoveValue(index)}
                                    cs={{ 
                                        padding: '6px 12px', 
                                        fontSize: '12px',
                                        backgroundColor: '#ff5722',
                                        borderColor: '#ff5722',
                                        color: 'white'
                                    }}
                                >
                                    Remove
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Navigation */}
            <div style={{ textAlign: 'center', marginTop: '30px' }}>
                <Button 
                    clickhandler={() => window.history.back()}
                    cs={{ padding: '10px 20px' }}
                >
                    Back
                </Button>
            </div>
        </div>
    );
};

export default TotalBillManager;
